// Search-state for the products index route. The route in AppRouter.tsx owns
// the validateSearch schema and stripSearchParams middleware; this hook is a
// thin, typed surface over TanStack's useSearch / useNavigate so consumers
// don't touch the router primitives directly. Tab / format / filter / sort
// changes reset page so previous-state pagination never bleeds into the new
// view. `formats` is an any-of array; empty means every format.

// External
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

// Internal
import { DEFAULT_PAGE_SIZE } from '@components/DataTable/constants';
import type { ProductPublishedStatus, ProductForm } from '../components/Product/types';

export interface ProductSearchParams {
  status: ProductPublishedStatus;
  formats: ProductForm[];
  q: string;
  page: number;
  perPage: number;
  sort: string;
  direction: 'asc' | 'desc' | '';
  catalogueIds: number[];
  dietTags: string[];
  allergenTags: string[];
  categories: string[];
  nutritionTags: string[];
  chefPick: boolean;
  servingTemperatures: string[];
  statuses: string[];
}

export const PRODUCTS_SEARCH_DEFAULTS: ProductSearchParams = {
  status: 'published',
  formats: [],
  q: '',
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  sort: '',
  direction: '',
  catalogueIds: [],
  dietTags: [],
  allergenTags: [],
  categories: [],
  nutritionTags: [],
  chefPick: false,
  servingTemperatures: [],
  statuses: [],
};

const PRODUCTS_ROUTE = '/products' as const;

const VALID_STATUSES: ReadonlySet<ProductPublishedStatus> = new Set(['published', 'unpublished']);
const VALID_FORMATS: ReadonlySet<ProductForm> = new Set([
  'buffet',
  'mini_buffet',
  'gathering_box',
  'meal_box',
  'treat_box',
]);
const VALID_DIRECTIONS: ReadonlySet<'asc' | 'desc'> = new Set(['asc', 'desc']);
const VALID_ACTIVE_STATUSES = new Set(['active', 'inactive']);

function toNumberArray(raw: unknown): number[] {
  if (Array.isArray(raw)) {
    return raw.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof raw === 'string' && raw.length > 0) {
    return raw.split(',').map(Number).filter((n) => Number.isFinite(n) && n > 0);
  }
  return [];
}

// Deduped: these values become chip keys and faceted-filter counts, and a
// hand-edited `?formats=buffet,buffet` would otherwise render two chips sharing
// one React key inside an AnimatePresence.
function toStringArray(raw: unknown, allow?: ReadonlySet<string>): string[] {
  const arr = Array.isArray(raw)
    ? raw.map(String)
    : typeof raw === 'string' && raw.length > 0
      ? raw.split(',')
      : [];
  return [...new Set(allow ? arr.filter((s) => allow.has(s)) : arr.filter(Boolean))];
}

// validateSearch handler — coerces unknown URL input into the typed shape.
export function parseProductsSearch(raw: Record<string, unknown>): ProductSearchParams {
  const status: ProductPublishedStatus = VALID_STATUSES.has(raw.status as ProductPublishedStatus)
    ? (raw.status as ProductPublishedStatus)
    : PRODUCTS_SEARCH_DEFAULTS.status;
  const page = Number(raw.page);
  const perPage = Number(raw.perPage);
  const direction = VALID_DIRECTIONS.has(raw.direction as 'asc' | 'desc')
    ? (raw.direction as 'asc' | 'desc')
    : PRODUCTS_SEARCH_DEFAULTS.direction;
  return {
    status,
    formats: toStringArray(raw.formats, VALID_FORMATS) as ProductForm[],
    q: typeof raw.q === 'string' ? raw.q : PRODUCTS_SEARCH_DEFAULTS.q,
    page: Number.isFinite(page) && page > 0 ? page : PRODUCTS_SEARCH_DEFAULTS.page,
    perPage:
      Number.isFinite(perPage) && perPage > 0
        ? perPage
        : PRODUCTS_SEARCH_DEFAULTS.perPage,
    sort: typeof raw.sort === 'string' ? raw.sort : PRODUCTS_SEARCH_DEFAULTS.sort,
    direction,
    catalogueIds: toNumberArray(raw.catalogueIds),
    dietTags: toStringArray(raw.dietTags),
    allergenTags: toStringArray(raw.allergenTags),
    categories: toStringArray(raw.categories),
    nutritionTags: toStringArray(raw.nutritionTags),
    chefPick: raw.chefPick === true || raw.chefPick === 'true',
    servingTemperatures: toStringArray(raw.servingTemperatures),
    statuses: toStringArray(raw.statuses, VALID_ACTIVE_STATUSES),
  };
}

export function useProductRouterSearch() {
  const search = useSearch({ from: PRODUCTS_ROUTE });
  const tanNavigate = useNavigate({ from: PRODUCTS_ROUTE });

  const navigate = useCallback(
    (updates: Partial<ProductSearchParams>) => {
      tanNavigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [tanNavigate],
  );

  return useMemo(
    () => ({
      search,
      navigate,
      setStatus: (status: ProductPublishedStatus) => navigate({ status, page: 1 }),
      setFormats: (formats: ProductForm[]) => navigate({ formats, page: 1 }),
      setQuery: (q: string) => navigate({ q, page: 1 }),
      setPage: (page: number) => navigate({ page }),
      setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
      setSort: (sort: string, direction: 'asc' | 'desc' | '') =>
        navigate({ sort, direction, page: 1 }),
      setCatalogueIds: (catalogueIds: number[]) => navigate({ catalogueIds, page: 1 }),
      setDietTags: (dietTags: string[]) => navigate({ dietTags, page: 1 }),
      setAllergenTags: (allergenTags: string[]) => navigate({ allergenTags, page: 1 }),
      setCategories: (categories: string[]) => navigate({ categories, page: 1 }),
      setNutritionTags: (nutritionTags: string[]) => navigate({ nutritionTags, page: 1 }),
      setChefPick: (chefPick: boolean) => navigate({ chefPick, page: 1 }),
      setServingTemperatures: (servingTemperatures: string[]) =>
        navigate({ servingTemperatures, page: 1 }),
      setStatuses: (statuses: string[]) => navigate({ statuses, page: 1 }),
    }),
    [search, navigate],
  );
}
